/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { api, apiBlob, mutatingPost, mutatingPut, type ApiError, type ApiResult } from "./client";
import type { ReferralStage } from "./referrals";
import { type Paged } from "./registry";

const BASE = "/v1/onboardings";

export const ONBOARDING_STATUSES = [
  "INVITED",
  "OPENED",
  "VERIFIED",
  "CONFIRMED",
  "SCREENED",
  "ENROLLED",
  "BOOKED",
  "COMPLETED",
  "NEEDS_CARE_TEAM",
  "PAUSED",
  "STOPPED",
  "EXPIRED",
] as const;
export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number];

export type MessageChannel = "SMS" | "EMAIL";

export const ATTENTION_REASONS = [
  "LANDLINE",
  "INVALID_CONTACT",
  "DOB_LOCKED",
  "DUPLICATE",
  "PROVISIONING_FAILED",
  "WAITLIST",
  "INTAKE_REQUESTED",
  "BOOKING_FAILED",
  "INSURANCE_REVIEW",
  "FAMILY_ASKED",
  "STAFF",
] as const;
export type AttentionReason = (typeof ATTENTION_REASONS)[number];

/** The reasons a coordinator may hand a family over for; nexus raises the rest itself. */
export const STAFF_HANDOFF_REASONS = ["STAFF", "FAMILY_ASKED", "INSURANCE_REVIEW"] as const;
export type StaffHandoffReason = (typeof STAFF_HANDOFF_REASONS)[number];

export const ONBOARDING_STEPS = [
  "DETAILS",
  "CONCERNS",
  "INSURANCE",
  "SAFETY",
  "ENROLL",
  "BOOK",
  "FORMS",
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export const CONCERNS = [
  "WORRIES_STRESS_ANXIETY",
  "SADNESS_DEPRESSION_GRIEF",
  "BEHAVIOR_ATTENTION_SCHOOL",
  "FAMILY_IDENTITY_RELATIONSHIPS",
  "VIOLENCE_TRAUMA",
  "PASSIVE_SI_SELF_HARM",
  "ACTIVE_DIVORCE_SEPARATION",
  "ACTIVE_SI",
  "OTHER",
] as const;
export type Concern = (typeof CONCERNS)[number];

export const INSURANCE_EXITS = [
  "ENTER_PLAN",
  "CHECKING",
  "COVERED",
  "CONFIRM_LATER",
  "OUT_OF_NETWORK",
  "NOT_ACTIVE",
  "REVIEW",
  "MEMBER_ID_NEEDED",
  "CARD_UNREADABLE",
  "NOT_FOUND",
  "UNMAPPED",
  "SELF_PAY",
] as const;
export type InsuranceExit = (typeof INSURANCE_EXITS)[number];

export const INSURANCE_DECISIONS = ["CONFIRMED", "ENTERED", "DISCOVERED", "CARD", "SELF_PAY"] as const;
export type InsuranceDecision = (typeof INSURANCE_DECISIONS)[number];

export const MESSAGE_KINDS = [
  "INVITE",
  "FOLLOW_UP",
  "INTAKE_CONFIRMATION",
  "CONSULT_CONFIRMATION",
  "SET_PASSWORD",
  "CARE_TEAM_HANDOFF",
  "ENROLLMENT_REVIEW",
  "SLOT_LOST",
] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

export const MESSAGE_STATUSES = [
  "QUEUED",
  "SENDING",
  "SENT",
  "FAILED",
  "SUPPRESSED",
  "SKIPPED",
  "CANCELLED",
  "UNKNOWN",
] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

export const DELIVERY_STATUSES = [
  "DELIVERED",
  "UNDELIVERED",
  "BOUNCED",
  "DROPPED",
  "DEFERRED",
  "SPAM",
] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export type AppointmentKind = "INTAKE" | "CONSULT";
export const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export const DAY_BLOCKS = ["MORNING", "AFTERNOON", "EVENING"] as const;
export type DayBlock = (typeof DAY_BLOCKS)[number];

export type ProgressStep = { step: OnboardingStep; state: "DONE" | "TODO" | "LOCKED"; editable: boolean };

export type ProviderCard = {
  providerId: string;
  initials: string;
  displayName: string;
  credential: string | null;
  roleLine: string;
};

export type ProvisioningMember = {
  userId: number;
  status: "PENDING" | "LINKED" | "REFUSED" | "FAILED";
};

export type Holder = { firstName: string | null; lastName: string | null; dateOfBirth: string | null };

/** Mirror of nexus OnboardingView as staff read it. */
export type OnboardingView = {
  id: string;
  status: OnboardingStatus;
  attentionReason: AttentionReason | null;
  progress: { steps: ProgressStep[]; resumeStep: OnboardingStep | null; impliedStatus: OnboardingStatus };
  caregiver: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    state: string | null;
  };
  child: { firstName: string | null; lastName: string | null; dateOfBirth: string | null };
  concerns: Concern[];
  concernsOther: string | null;
  consultOnly: boolean;
  insurance: { decision: InsuranceDecision | null; holder: Holder | null };
  safetyConfirmed: boolean | null;
  enrolled: boolean;
  provisioning: {
    status: "PENDING" | "LINKED" | "FAILED";
    members: ProvisioningMember[] | null;
  } | null;
  booking: {
    booked: boolean;
    appointmentKind: AppointmentKind | null;
    state: "REQUESTED" | "CONFLICT" | "FAILED" | null;
    startsAt: string | null;
    zone: string;
    provider: ProviderCard | null;
    contactPhone: string | null;
    formsOpened: boolean;
  };
  smsConsented: boolean;
  version: number;
};

export type CostEstimate = {
  intakeRateCents: number | null;
  sessionRateCents: number | null;
  copayCents: number | null;
  coinsurancePercent: number | null;
  deductibleCents: number | null;
  remainingDeductibleCents: number | null;
  intakeCostCents: number | null;
  sessionCostCents: number | null;
  postDeductibleSessionCostCents: number | null;
};

export type Coverage = {
  status: "PENDING" | "ELIGIBLE" | "INELIGIBLE" | "UNKNOWN" | "SELF_PAY";
  reason: CoverageReason | null;
  payerId: string | null;
  payerDisplayName: string | null;
  payerNameRaw: string | null;
  inNetwork: boolean;
  memberId: string | null;
  groupNumber: string | null;
  cost: CostEstimate | null;
  checkedAt: string | null;
};

export type PayerChoice = {
  kind: "PAYER" | "OTHER_PLAN" | "SELF_PAY";
  displayName: string;
  payerId: string | null;
  inNetwork: boolean;
};

export type CardSide = "FRONT" | "BACK";

export type CardOutcome = "QUEUED" | "READ" | "PARTIAL" | "UNREADABLE" | "UNAVAILABLE";

export type InsuranceCard = {
  side: CardSide;
  outcome: CardOutcome;
  contentType: string;
  url: string | null;
  readAt: string | null;
  extractedPayerName: string | null;
  extractedMemberIdMasked: string | null;
  extractedGroup: string | null;
  coverageCheckId: string | null;
};

export type ClaimKind = "DECLARE" | "DISCOVER" | "MEMBER" | "CARD" | "SELF_PAY";
export type ClaimSource = "ENTERED" | "CONFIRMED_HINT" | "DISCOVERED" | "CARD" | "SCREENER_ANSWER" | "STAFF" | "REFERRAL";
export type CoverageReason =
  | "NOT_DISCOVERED"
  | "MEMBER_ID_NEEDED"
  | "CARD_UNREADABLE"
  | "UNMAPPED_PAYER"
  | "NO_FEE_SCHEDULE"
  | "INACTIVE"
  | "VENDOR_ERROR"
  | "PENDING_VENDOR"
  | "OUT_OF_NETWORK";
export type VendorStep = "DISCOVERY" | "VERIFICATION";
export type CheckRunState = "QUEUED" | "RUNNING" | "DONE";

/** One coverage check as the ledger reads it (mirror of nexus InsuranceView.CheckView). */
export type InsuranceCheck = {
  id: string;
  claimKind: ClaimKind;
  source: ClaimSource;
  subjectKey: string;
  vendorStep: VendorStep | null;
  status: Coverage["status"];
  reason: CoverageReason | null;
  generic: boolean;
  payerBasis: "VERIFIED" | "DECLARED" | "NONE";
  payerId: string | null;
  payerDisplayName: string | null;
  payerNameRaw: string | null;
  memberIdMasked: string | null;
  groupNumber: string | null;
  cost: CostEstimate | null;
  vendorRequestId: string | null;
  runState: CheckRunState;
  attemptCount: number;
  nextAttemptAt: string | null;
  askedAt: string | null;
  checkedAt: string | null;
  error: string | null;
  superseded: boolean;
  subjectName: string | null;
  askedByUserId: number | null;
  askedByName: string | null;
};

/** Mirror of nexus InsuranceView (staff). */
export type InsuranceView = {
  decision: InsuranceDecision | null;
  holder: Holder | null;
  exit: InsuranceExit;
  coverage: Coverage | null;
  catalog: PayerChoice[];
  cards: InsuranceCard[];
  /** Every check, oldest first; empty on the family's view. */
  checks: InsuranceCheck[];
};

export type OnboardingEvent = {
  field: string;
  oldValue: string | null;
  newValue: string | null;
  detail: Record<string, unknown> | null;
  actorKind: "STAFF" | "FAMILY" | "SYSTEM";
  actorUserId: number | null;
  /** The staff member's name, when nexus knows it. */
  actorName: string | null;
  changedAt: string;
};

export type OnboardingMessage = {
  id: string;
  kind: string;
  sequence: number | null;
  channel: MessageChannel;
  scheduledAt: string | null;
  status: MessageStatus;
  skipReason: string | null;
  lastError: string | null;
  deliveryStatus: DeliveryStatus | null;
  sentAt: string | null;
  createdAt: string | null;
  /** An email's subject as first sent. */
  subject: string | null;
  /** What it said as first sent, its links as a word; null until it is rendered. */
  body: string | null;
};

export type StepTimes = {
  openedAt: string | null;
  verifiedAt: string | null;
  detailsSavedAt: string | null;
  concernsSavedAt: string | null;
  insuranceSavedAt: string | null;
  safetySavedAt: string | null;
  enrolledAt: string | null;
  bookedAt: string | null;
  formsOpenedAt: string | null;
};

/** Mirror of nexus StaffOnboardingView: one onboarding as the coordinator works it. */
export type StaffOnboarding = {
  onboarding: OnboardingView;
  referralId: number;
  linkExpiresAt: string | null;
  dobAttempts: number;
  dobLockedAt: string | null;
  attentionDetail: string | null;
  pausedAt: string | null;
  pausedBy: number | null;
  zone: string;
  familyId: number | null;
  appointmentId: string | null;
  healthieAppointmentId: string | null;
  attentionSince: string | null;
  optedOut: MessageChannel[];
  reachable: MessageChannel[];
  /** The stage the referral list reads for this family. */
  stage: ReferralStage;
  preferences: { weekdays: Weekday[]; blocks: DayBlock[]; savedAt: string } | null;
  note: { text: string; byName: string | null; at: string } | null;
  steps: StepTimes;
  insurance: InsuranceView;
  createdAt: string;
  updatedAt: string;
  events: OnboardingEvent[];
  messages: OnboardingMessage[];
};

export type OnboardingSummary = {
  id: string;
  referralId: number;
  status: OnboardingStatus;
  attentionReason: AttentionReason | null;
  caregiverFirstName: string | null;
  caregiverLastName: string | null;
  childFirstName: string | null;
  childLastName: string | null;
  resumeStep: OnboardingStep | null;
  consultOnly: boolean;
  attentionSince: string | null;
  optedOut: MessageChannel[];
  pausedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

const NEEDS_CARE_TEAM_FILTER = 'status eq "NEEDS_CARE_TEAM"';

/** How many families wait on a coordinator: the Referrals nav badge. */
export async function needsCareTeamCount(): Promise<ApiResult<number>> {
  const params = new URLSearchParams({ page: "0", pageSize: "1", filter: NEEDS_CARE_TEAM_FILTER });
  const page = await api<Paged<OnboardingSummary>>(`${BASE}?${params.toString()}`);
  return page.ok ? { ok: true, value: page.value.total } : page;
}

export function onboardingDetail(id: string): Promise<ApiResult<StaffOnboarding>> {
  return api(`${BASE}/${encodeURIComponent(id)}`);
}

export function onboardingLink(id: string): Promise<ApiResult<{ link: string; expiresAt: string }>> {
  return api(`${BASE}/${encodeURIComponent(id)}/link`);
}

export function cardImage(url: string, signal?: AbortSignal): Promise<ApiResult<Blob>> {
  return apiBlob(url, signal === undefined ? {} : { signal });
}

export function inviteFamily(referralId: number, idempotencyKey: string): Promise<ApiResult<OnboardingView>> {
  return api(`${BASE}/actions/invite`, mutatingPost({ referralId }, idempotencyKey));
}

export type OnboardingAction =
  | { action: "pause" }
  | { action: "resume" }
  | { action: "stop" }
  | { action: "unlock-dob" }
  | { action: "resend-link" }
  | { action: "hand-to-care-team"; reason: StaffHandoffReason; note: string | null }
  | { action: "attach-family"; familyId: number };

export function runAction(
  id: string,
  { action, ...body }: OnboardingAction,
  idempotencyKey: string,
): Promise<ApiResult<OnboardingView>> {
  return api(
    `${BASE}/${encodeURIComponent(id)}/actions/${action}`,
    mutatingPost(body, idempotencyKey),
  );
}

/**
 * The onboarding a 409 answers beside its error, so the caller re-renders from where it now
 * stands instead of asking again; null for any other failure.
 */
export function conflictView(error: ApiError): OnboardingView | null {
  if (error.httpStatus !== 409 || typeof error.body !== "object" || error.body === null) {
    return null;
  }
  const onboarding = (error.body as { onboarding?: OnboardingView }).onboarding;
  return onboarding ?? null;
}

export type DetailsBody = {
  caregiver: { first: string; last: string; email: string; phone: string; state: string };
  child: { first: string; last: string };
};

export function saveDetails(id: string, body: DetailsBody, version: number): Promise<ApiResult<OnboardingView>> {
  return api(`${BASE}/${encodeURIComponent(id)}/details`, mutatingPut({ ...body, version }));
}

/** {@code basedOn} is when the note being edited was written, so a colleague's newer words stay. */
export function saveNote(id: string, note: string | null, basedOn: string | null): Promise<ApiResult<OnboardingView>> {
  return api(`${BASE}/${encodeURIComponent(id)}/note`, mutatingPut({ note, basedOn }));
}

export function saveConcerns(
  id: string,
  concerns: Concern[],
  other: string | null,
  version: number,
): Promise<ApiResult<OnboardingView>> {
  return api(
    `${BASE}/${encodeURIComponent(id)}/concerns`,
    mutatingPut({ concerns, other, version }),
  );
}

export function saveSafety(id: string, confirmed: boolean, version: number): Promise<ApiResult<OnboardingView>> {
  return api(`${BASE}/${encodeURIComponent(id)}/safety`, mutatingPut({ confirmed, version }));
}

export type InsuranceBody =
  | { decision: "CONFIRMED" }
  | { decision: "SELF_PAY" }
  | {
      decision: "ENTERED";
      payerId: string | null;
      payerNameRaw: string | null;
      memberId: string;
      groupNumber: string | null;
      holder: Holder | null;
    };

export type InsuranceWritten = {
  exit: InsuranceExit;
  coverage: Coverage | null;
  onboarding: OnboardingView;
};

export function saveInsurance(
  id: string,
  body: InsuranceBody,
  version: number,
): Promise<ApiResult<InsuranceWritten>> {
  return api(`${BASE}/${encodeURIComponent(id)}/insurance`, mutatingPut({ ...body, version }));
}

export function recheckInsurance(id: string, idempotencyKey: string): Promise<ApiResult<InsuranceWritten>> {
  return api(`${BASE}/${encodeURIComponent(id)}/insurance/recheck`, mutatingPost({}, idempotencyKey));
}

export type Enrollment = {
  enrollment: {
    state: "ENROLLED" | "HANDOFF";
    familyId: number | null;
    provisioning: OnboardingView["provisioning"];
  };
  progress: OnboardingView["progress"];
};

export function enrollFamily(
  id: string,
  version: number,
  confirmNew: boolean,
  idempotencyKey: string,
): Promise<ApiResult<Enrollment>> {
  return api(
    `${BASE}/${encodeURIComponent(id)}/enroll`,
    mutatingPost({ version, confirmNew }, idempotencyKey),
  );
}

export type GridCell = { weekday: Weekday; hour: number; fillable: boolean };

export type ScheduleGrid = {
  kind: AppointmentKind;
  zone: string;
  earliestStart: string;
  cells: GridCell[];
  onboarding: OnboardingView;
};

export type ProviderSpecialty =
  | "THERAPIST"
  | "PSYCHIATRIST"
  | "PHYSICIAN"
  | "PEDIATRICIAN"
  | "PARENT_COACH"
  | "DIETITIAN";

/** What sets one provider apart, as a coordinator picks between them. */
export type ProviderFacts = {
  earliestOpen: string | null;
  specialty: ProviderSpecialty | null;
  licensedIn: string | null;
};

/** What every provider offered shares, said once above them. */
export type SharedFacts = { licensedIn: string | null; planName: string | null };

export type ProviderOffer = { provider: ProviderCard; starts: string[]; facts: ProviderFacts };

export type Alternative = {
  weekday: Weekday;
  hour: number;
  provider: ProviderCard;
  starts: string[];
  label: string;
};

export type Booked = { booking: OnboardingView["booking"]; onboarding: OnboardingView };

export function availability(id: string, kind: AppointmentKind): Promise<ApiResult<ScheduleGrid>> {
  return api(`${BASE}/${encodeURIComponent(id)}/availability?kind=${kind}`);
}

export function availableProviders(
  id: string,
  kind: AppointmentKind,
  weekday: Weekday,
  hour: number,
): Promise<ApiResult<{ providers: ProviderOffer[]; shared: SharedFacts; onboarding: OnboardingView }>> {
  const params = new URLSearchParams({ kind, weekday, hour: String(hour) });
  return api(`${BASE}/${encodeURIComponent(id)}/availability/providers?${params.toString()}`);
}

export function book(
  id: string,
  body: { providerId: string; startsAt: string; kind: AppointmentKind },
  idempotencyKey: string,
): Promise<ApiResult<Booked>> {
  return api(`${BASE}/${encodeURIComponent(id)}/book`, mutatingPost(body, idempotencyKey));
}

/** The alternatives a SLOT_TAKEN conflict carries; empty for any other failure. */
export function alternativesOf(error: ApiError): Alternative[] {
  if (error.code !== "SLOT_TAKEN" || typeof error.body !== "object" || error.body === null) {
    return [];
  }
  return (error.body as { alternatives?: Alternative[] }).alternatives ?? [];
}
